import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import EmployeeProfile from './EmployeeProfile';
import { isSelfServiceAssignmentEnabled } from '../../../components/services/service-staff-assignment/employeeSelfServiceAssignmentUtils';
import {
  buildEmployeeSlugFields,
  ensureCompanyEmployeeSlug,
  getCompanyEmployeeUrlSegment,
  resolveCompanyEmployee,
} from '../../../shared/employees/companyEmployeeRouting';
import { format } from 'date-fns';
import BackButton from '../../../shared/components/back-button/BackButton';
import { PageSkeleton } from '../../../components/loading/skeletons';
import { listEmployees, upsertEmployee } from '../../../api/companyRecords';
import './styles/employee-profile-workspace.css';

const EmployeeProfileContainer = ({ selfViewOnly = false, requireSelfAccess = false }) => {
  const { employeeId: employeeParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, companyData } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { setNotification } = useNotification();
  const { hasPermission } = usePermissions();
  const canViewEmployees = hasPermission(PERMISSIONS.EMPLOYEES.VIEW);
  const canEditEmployee = user?.role === 'owner' && hasPermission(PERMISSIONS.EMPLOYEES.EDIT);

  const isSelfProfile = Boolean(user?.uid && employee?.id && user.uid === employee.id);
  const selfServiceEnabled = isSelfServiceAssignmentEnabled(companyData);

  const fetchEmployeeData = useCallback(async () => {
    if (!employeeParam || !companyData?.id) return;

    setLoading(true);
    setError(null);

    try {
      const resolved = await resolveCompanyEmployee(
        companyData.id,
        employeeParam,
        companyData.ownerId
      );
      if (!resolved?.employee) {
        setError('Employee not found');
        setEmployee(null);
        return;
      }

      let nextEmployee = {
        ...resolved.employee,
        companyId: companyData.id,
      };

      if (!nextEmployee.slug) {
        const slug = await ensureCompanyEmployeeSlug(
          companyData.id,
          nextEmployee.id,
          nextEmployee
        );
        nextEmployee = { ...nextEmployee, slug };
      }

      setEmployee(nextEmployee);

      const segment = getCompanyEmployeeUrlSegment(nextEmployee);
      if (segment && segment !== employeeParam) {
        navigate(`/app/overview/employees/profile/${segment}`, {
          replace: true,
          state: location.state,
        });
      }
    } catch (err) {
      console.error('Error fetching employee:', err);
      setError('Failed to fetch employee data. Please try again.');
      setEmployee(null);
    } finally {
      setLoading(false);
    }
  }, [employeeParam, companyData, user, navigate, location.state]);

  useEffect(() => {
    fetchEmployeeData();
  }, [fetchEmployeeData]);

  const handleBack = () => {
    const returnTo = location.state?.returnTo;
    if (
      typeof returnTo === 'string' &&
      returnTo.startsWith('/app/') &&
      !returnTo.startsWith('//')
    ) {
      navigate(returnTo);
      return;
    }
    if (selfViewOnly || (isSelfProfile && !canViewEmployees)) {
      navigate('/app/overview/settings', {
        state: {
          section: selfServiceEnabled ? 'my-services' : 'payouts',
          parent: selfServiceEnabled ? 'my-services' : 'payouts',
        },
      });
      return;
    }
    navigate('/app/overview/company-management');
  };

  const handleUpdateEmployee = async (updatedData) => {
    if (!employee) return;

    try {
      setSaving(true);
      const existingEmployees = await listEmployees({ companyId: companyData.id });
      const slugFields = buildEmployeeSlugFields(
        { ...employee, ...updatedData },
        existingEmployees,
        employee.id
      );

      const nextData = {
        ...updatedData,
        ...slugFields,
        updatedAt: new Date().toISOString(),
        permissions: updatedData.permissions || [],
        role: String(updatedData.role || employee.role || '').toLowerCase(),
      };

      await upsertEmployee({
        companyId: companyData.id,
        id: employee.id,
        employee: nextData,
      });

      const nextEmployee = {
        ...employee,
        ...nextData,
      };
      setEmployee(nextEmployee);

      const segment = getCompanyEmployeeUrlSegment(nextEmployee);
      if (segment && segment !== employeeParam) {
        navigate(`/app/overview/employees/profile/${segment}`, {
          replace: true,
          state: location.state,
        });
      }

      setNotification({
        open: true,
        message: 'Employee updated successfully',
        severity: 'success',
      });
      return true;
    } catch (updateError) {
      console.error('Error updating employee:', updateError);
      setNotification({
        open: true,
        message: 'Failed to update employee. Please try again.',
        severity: 'error',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently joined';

    if (timestamp.seconds) {
      return format(new Date(timestamp.seconds * 1000), 'MM/dd/yyyy');
    }

    if (timestamp instanceof Date) {
      return format(timestamp, 'MM/dd/yyyy');
    }

    return 'Recently joined';
  };

  if (loading) {
    return (
      <div className="employee-profile-page">
        <PageSkeleton padded variant="form" label="Loading employee" />
      </div>
    );
  }

  if (requireSelfAccess && employee && user?.uid && employee.id !== user.uid) {
    return (
      <div className="employee-profile-page">
        <div className="employee-profile-state">You don't have permission to view this page.</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="employee-profile-page">
        <div className="employee-profile-state">
          <div>{error}</div>
          <div className="employee-profile-state__detail">
            The employee you're looking for could not be found.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-profile-page">
      <div className="employee-profile-page__toolbar">
        <BackButton
          label={selfViewOnly ? 'Back to Settings' : 'Back to Employees'}
          className="back-btn--flush"
          onClick={handleBack}
        />
      </div>
      <EmployeeProfile
        employee={employee}
        onSave={handleUpdateEmployee}
        saving={saving}
        canEdit={canEditEmployee}
        formatDate={formatDate}
        currentUser={user}
        selfViewOnly={selfViewOnly}
      />
    </div>
  );
};

export default EmployeeProfileContainer;
